/**
 * Finance API hooks. One file per app to keep imports tidy.
 *
 * All endpoints under /api/apps/finance/* (apiBase from the app manifest).
 * This file grows block-by-block: FIN-9 lands accounts, categories and
 * settings; FIN-10+ extend it with transactions, imports, rules, transfers,
 * budgets and analytics. Keep the section ordering stable.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FinanceAccountType,
  FinanceAccountGroup,
  FinanceCategoryKind,
  FinanceCategorizedBy,
  FinanceRuleCriteria,
  FinanceRuleAction,
} from '@k-os/core';
import { apiGet, apiSend } from './client';

/** Shared query-key root so a single invalidation can target the whole app. */
const FIN = 'finance' as const;

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

export interface FinanceAccountDto {
  id: string;
  workspaceId: string;
  name: string;
  type: FinanceAccountType;
  group: FinanceAccountGroup;
  parentAccountId: string | null;
  currency: string;
  ownerLabel: string;
  initialBalance: number;
  initialBalanceDate: string | null;
  currentBalance: number;
  currentBalanceEur: number;
  lastTransactionDate: string | null;
  archivedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface FinanceAccountInput {
  name: string;
  type: FinanceAccountType;
  group: FinanceAccountGroup;
  parentAccountId?: string | null;
  currency?: string;
  ownerLabel?: string;
  initialBalance?: number;
  initialBalanceDate?: string | null;
}

export function useFinanceAccounts(options: { includeArchived?: boolean } = {}) {
  const includeArchived = options.includeArchived ?? false;
  return useQuery({
    queryKey: [FIN, 'accounts', { includeArchived }],
    queryFn: () =>
      apiGet<{ accounts: FinanceAccountDto[] }>(
        includeArchived
          ? '/apps/finance/accounts?includeArchived=true'
          : '/apps/finance/accounts',
      ),
    select: (r) => r.accounts,
    staleTime: 30_000,
  });
}

export function useCreateFinanceAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FinanceAccountInput) =>
      apiSend<{ account: FinanceAccountDto }>('POST', '/apps/finance/accounts', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FIN, 'accounts'] }),
  });
}

export function usePatchFinanceAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<FinanceAccountInput> }) =>
      apiSend<{ account: FinanceAccountDto }>(
        'PATCH',
        `/apps/finance/accounts/${vars.id}`,
        vars.patch,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FIN, 'accounts'] }),
  });
}

export function useArchiveFinanceAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ account: FinanceAccountDto }>('DELETE', `/apps/finance/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FIN, 'accounts'] }),
  });
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export interface FinanceCategoryDto {
  id: string;
  workspaceId: string;
  name: string;
  parentId: string | null;
  kind: FinanceCategoryKind;
  active: boolean;
  archivedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface FinanceCategoryInput {
  name: string;
  kind: FinanceCategoryKind;
  parentId?: string | null;
  active?: boolean;
}

export function useFinanceCategories(options: { includeInactive?: boolean } = {}) {
  const includeInactive = options.includeInactive ?? false;
  return useQuery({
    queryKey: [FIN, 'categories', { includeInactive }],
    queryFn: () =>
      apiGet<{ categories: FinanceCategoryDto[] }>(
        includeInactive
          ? '/apps/finance/categories?includeInactive=true'
          : '/apps/finance/categories',
      ),
    select: (r) => r.categories,
    staleTime: 30_000,
  });
}

export function useCreateFinanceCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FinanceCategoryInput) =>
      apiSend<{ category: FinanceCategoryDto }>('POST', '/apps/finance/categories', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FIN, 'categories'] }),
  });
}

export function usePatchFinanceCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<FinanceCategoryInput> }) =>
      apiSend<{ category: FinanceCategoryDto }>(
        'PATCH',
        `/apps/finance/categories/${vars.id}`,
        vars.patch,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FIN, 'categories'] }),
  });
}

export function useArchiveFinanceCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ category: FinanceCategoryDto }>('DELETE', `/apps/finance/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FIN, 'categories'] }),
  });
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export interface FinanceSettingsDto {
  id: string;
  workspaceId: string;
  transferWindowDays: number;
  transferEurTolerancePct: number;
  professionalCategoryId: string | null;
  reimbursementCategoryId: string | null;
  updatedAt: string;
}

export interface FinanceSettingsInput {
  transferWindowDays?: number;
  transferEurTolerancePct?: number;
  professionalCategoryId?: string | null;
  reimbursementCategoryId?: string | null;
}

export function useFinanceSettings() {
  return useQuery({
    queryKey: [FIN, 'settings'],
    queryFn: () => apiGet<{ settings: FinanceSettingsDto }>('/apps/finance/settings'),
    select: (r) => r.settings,
    staleTime: 60_000,
  });
}

export function usePatchFinanceSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: FinanceSettingsInput) =>
      apiSend<{ settings: FinanceSettingsDto }>('PATCH', '/apps/finance/settings', patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FIN, 'settings'] }),
  });
}

// ---------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------

export interface FinanceTransactionDto {
  id: string;
  workspaceId: string;
  accountId: string;
  date: string;
  description: string;
  amount: number;
  amountEur: number;
  categoryId: string | null;
  categorizedBy: FinanceCategorizedBy | null;
  transferId: string | null;
  bankCategory: string;
  bankType: string;
  balanceAfter: number | null;
  importBatchId: string | null;
  signature: string;
  notes: string;
  archivedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionListParams {
  accountId?: string;
  categoryId?: string;
  includeSubtree?: boolean;
  from?: string;
  to?: string;
  uncategorized?: boolean;
  /** default includes both; 'only' = transfer legs, 'exclude' = hide legs */
  transferLegs?: 'only' | 'exclude';
  q?: string;
  /** Signed EUR amount range. Max=0 → only expenses (≤0); Min=0 → only income (≥0). */
  amountMin?: number;
  amountMax?: number;
  importBatchId?: string;
  limit?: number;
  offset?: number;
}

function transactionsQueryString(params: TransactionListParams): string {
  const sp = new URLSearchParams();
  if (params.accountId) sp.set('accountId', params.accountId);
  if (params.categoryId) sp.set('categoryId', params.categoryId);
  if (params.includeSubtree) sp.set('includeSubtree', 'true');
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  if (params.uncategorized) sp.set('uncategorized', 'true');
  if (params.transferLegs) sp.set('transferLegs', params.transferLegs);
  if (params.q) sp.set('q', params.q);
  if (params.amountMin !== undefined) sp.set('amountMin', String(params.amountMin));
  if (params.amountMax !== undefined) sp.set('amountMax', String(params.amountMax));
  if (params.importBatchId) sp.set('importBatchId', params.importBatchId);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.offset !== undefined) sp.set('offset', String(params.offset));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function useFinanceTransactions(params: TransactionListParams = {}) {
  return useQuery({
    queryKey: [FIN, 'transactions', params],
    queryFn: () =>
      apiGet<{ transactions: FinanceTransactionDto[]; total: number }>(
        `/apps/finance/transactions${transactionsQueryString(params)}`,
      ),
    staleTime: 10_000,
  });
}

/** Max rows a "select all matching" / bulk action can span (server + bulk-endpoint cap). */
export const TRANSACTIONS_BULK_CAP = 500;

/**
 * Fetch every transaction matching the given filter (up to the bulk cap), for
 * "select all matching" across pages. Forces offset 0 + the cap limit.
 */
export async function fetchFilteredTransactions(
  params: TransactionListParams,
): Promise<{ transactions: FinanceTransactionDto[]; total: number }> {
  return apiGet<{ transactions: FinanceTransactionDto[]; total: number }>(
    `/apps/finance/transactions${transactionsQueryString({
      ...params,
      limit: TRANSACTIONS_BULK_CAP,
      offset: 0,
    })}`,
  );
}

export interface TransactionPatchInput {
  date?: string;
  description?: string;
  amount?: number;
  amountEur?: number;
  categoryId?: string | null;
  notes?: string;
}

export function usePatchFinanceTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: TransactionPatchInput }) =>
      apiSend<{ transaction: FinanceTransactionDto }>(
        'PATCH',
        `/apps/finance/transactions/${vars.id}`,
        vars.patch,
      ),
    onSuccess: () => {
      // Categorizing changes the uncategorized count → refresh the Review badge.
      qc.invalidateQueries({ queryKey: [FIN, 'transactions'] });
      qc.invalidateQueries({ queryKey: [FIN, 'review-counts'] });
    },
  });
}

/** Archive (soft-delete) a single transaction. Transfer legs return 409. */
export function useArchiveFinanceTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ transaction: FinanceTransactionDto }>(
        'DELETE',
        `/apps/finance/transactions/${id}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FIN, 'transactions'] });
      qc.invalidateQueries({ queryKey: [FIN, 'review-counts'] });
      // Removing a transaction changes the account balance.
      qc.invalidateQueries({ queryKey: [FIN, 'accounts'] });
    },
  });
}

/** Move one or more transactions to another account (parent ↔ child). */
export function useMoveFinanceTransactions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { transactionIds: string[]; targetAccountId: string }) =>
      apiSend<{ moved: number }>('POST', '/apps/finance/transactions/move', vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FIN, 'transactions'] });
      // Moving shifts balances between accounts.
      qc.invalidateQueries({ queryKey: [FIN, 'accounts'] });
      qc.invalidateQueries({ queryKey: [FIN, 'review-counts'] });
    },
  });
}

export function useBulkCategorizeTransactions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { transactionIds: string[]; categoryId: string }) =>
      apiSend<{ updated: number }>(
        'POST',
        '/apps/finance/transactions/bulk-categorize',
        vars,
      ),
    onSuccess: () => {
      // Clearing an uncategorized group must drop the Review badge immediately.
      qc.invalidateQueries({ queryKey: [FIN, 'transactions'] });
      qc.invalidateQueries({ queryKey: [FIN, 'review-counts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Imports
// ---------------------------------------------------------------------------

export interface ImportRowInput {
  date: string; // ISO YYYY-MM-DD
  description: string;
  amount: number; // signed, native currency
  amountEur?: number;
  bankCategory?: string;
  bankType?: string;
  balanceAfter?: number;
}

export interface ImportPreviewRow extends ImportRowInput {
  signature: string;
  duplicate: boolean;
  needsEur?: boolean;
}

export interface ImportPreviewResult {
  rows: ImportPreviewRow[];
  duplicateCount: number;
}

export interface ImportCommitResult {
  batchId: string;
  inserted: number;
  autoCategorized: number;
  transfersConfirmed: number;
  transfersSuggested: number;
}

export interface ImportBatchDto {
  id: string;
  workspaceId: string;
  accountId: string;
  filename: string;
  columnMapping: Record<string, unknown>;
  rowCount: number;
  duplicateCount: number;
  createdBy: string;
  createdAt: string;
}

/** Newest-first batch list — the wizard reads [0].columnMapping to prefill. */
export function useFinanceImportBatches(accountId: string | undefined) {
  return useQuery({
    queryKey: [FIN, 'imports', { accountId: accountId ?? null }],
    queryFn: () =>
      apiGet<{ batches: ImportBatchDto[] }>(
        accountId
          ? `/apps/finance/imports?accountId=${accountId}`
          : '/apps/finance/imports',
      ),
    select: (r) => r.batches,
    enabled: !!accountId,
    staleTime: 30_000,
  });
}

export function usePreviewImport() {
  return useMutation({
    mutationFn: (vars: { accountId: string; rows: ImportRowInput[] }) =>
      apiSend<ImportPreviewResult>('POST', '/apps/finance/imports/preview', vars),
  });
}

export function useCommitImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      accountId: string;
      filename: string;
      columnMapping: Record<string, unknown>;
      rows: ImportRowInput[];
    }) => apiSend<ImportCommitResult>('POST', '/apps/finance/imports', vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FIN, 'transactions'] });
      qc.invalidateQueries({ queryKey: [FIN, 'imports'] });
      qc.invalidateQueries({ queryKey: [FIN, 'accounts'] });
      // Import changes uncategorized + suggested transfer counts.
      qc.invalidateQueries({ queryKey: [FIN, 'review-counts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

export interface FinanceRuleDto {
  id: string;
  workspaceId: string;
  criteria: FinanceRuleCriteria;
  action: FinanceRuleAction;
  priority: number;
  active: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface CreateRuleInput {
  criteria: FinanceRuleCriteria;
  action: FinanceRuleAction;
  priority?: number;
  active?: boolean;
  applyNow?: boolean;
}

export interface UpdateRuleInput {
  criteria?: FinanceRuleCriteria;
  action?: FinanceRuleAction;
  priority?: number;
  active?: boolean;
}

/** List categorization rules (ordered by priority, then created). */
export function useFinanceRules(options: { includeArchived?: boolean } = {}) {
  const includeArchived = options.includeArchived ?? false;
  return useQuery({
    queryKey: [FIN, 'rules', { includeArchived }],
    queryFn: () =>
      apiGet<{ rules: FinanceRuleDto[] }>(
        includeArchived
          ? '/apps/finance/rules?includeArchived=true'
          : '/apps/finance/rules',
      ),
    select: (r) => r.rules,
  });
}

/** Create a rule; when `applyNow`, the response includes `applied` (count). */
export function useCreateFinanceRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRuleInput) =>
      apiSend<{ rule: FinanceRuleDto; applied?: number }>(
        'POST',
        '/apps/finance/rules',
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FIN, 'rules'] });
      qc.invalidateQueries({ queryKey: [FIN, 'transactions'] });
      // applyNow back-fills categories → uncategorized count changes.
      qc.invalidateQueries({ queryKey: [FIN, 'review-counts'] });
    },
  });
}

/** Update an existing rule (match field/type, pattern, category, priority, active). */
export function useUpdateFinanceRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: UpdateRuleInput }) =>
      apiSend<{ rule: FinanceRuleDto }>(
        'PATCH',
        `/apps/finance/rules/${vars.id}`,
        vars.patch,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FIN, 'rules'] }),
  });
}

/** Archive (soft-delete) a rule. */
export function useArchiveFinanceRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ rule: FinanceRuleDto }>('DELETE', `/apps/finance/rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FIN, 'rules'] }),
  });
}

/** Run all active rules over every uncategorized transaction; returns the count matched. */
export function useRunFinanceRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiSend<{ matched: number; categorized: number; moved: number; moveErrors: number; byRule: Record<string, number> }>(
        'POST',
        '/apps/finance/rules/run',
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FIN, 'rules'] });
      qc.invalidateQueries({ queryKey: [FIN, 'transactions'] });
      qc.invalidateQueries({ queryKey: [FIN, 'review-counts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Transfers (FIN-11)
// ---------------------------------------------------------------------------

export interface TransferLegDto {
  id: string;
  accountId: string;
  accountName: string;
  date: string;
  description: string;
  amount: number;
  amountEur: number;
}

export interface FinanceTransferDto {
  id: string;
  workspaceId: string;
  status: 'suggested' | 'confirmed';
  matchedBy: string;
  /** External (one-sided) transfer counterparty label; null for internal transfers. */
  counterparty: string | null;
  date: string;
  amountEur: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Null on an external transfer whose only leg is the incoming (to) side. */
  from: TransferLegDto | null;
  /** Null on an external transfer whose only leg is the outgoing (from) side. */
  to: TransferLegDto | null;
}

export function useFinanceTransfers(status: 'suggested' | 'confirmed') {
  return useQuery({
    queryKey: [FIN, 'transfers', { status }],
    queryFn: () =>
      apiGet<{ transfers: FinanceTransferDto[] }>(
        `/apps/finance/transfers?status=${status}`,
      ),
    select: (r) => r.transfers,
    staleTime: 10_000,
  });
}

/** Invalidate everything a transfer mutation can touch (both leg states, the
 *  transactions list whose leg flags change, accounts balances, and counts). */
function invalidateTransfers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [FIN, 'transfers'] });
  qc.invalidateQueries({ queryKey: [FIN, 'transactions'] });
  qc.invalidateQueries({ queryKey: [FIN, 'accounts'] });
  qc.invalidateQueries({ queryKey: [FIN, 'review-counts'] });
}

export function useConfirmTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ transfer: FinanceTransferDto }>(
        'POST',
        `/apps/finance/transfers/${id}/confirm`,
        {},
      ),
    onSuccess: () => invalidateTransfers(qc),
  });
}

export function useRejectTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ transfer: FinanceTransferDto }>(
        'POST',
        `/apps/finance/transfers/${id}/reject`,
        {},
      ),
    onSuccess: () => invalidateTransfers(qc),
  });
}

export function useUnlinkTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ transfer: FinanceTransferDto }>('DELETE', `/apps/finance/transfers/${id}`),
    onSuccess: () => invalidateTransfers(qc),
  });
}

/** Create a manual confirmed transfer from a (negative-leg, positive-leg) pair. */
export function useCreateTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { fromTransactionId: string; toTransactionId: string }) =>
      apiSend<{ transfer: FinanceTransferDto }>('POST', '/apps/finance/transfers', vars),
    onSuccess: () => invalidateTransfers(qc),
  });
}

/** Result of `useMarkExternalTransfer`: how many of the requested legs were
 *  actually marked, and — on partial failure — the first distinct error so
 *  the modal can summarize what went wrong. */
export interface MarkExternalTransferResult {
  requested: number;
  marked: number;
  failed: number;
  /** First distinct failure, for display via `financeErrorMessage`. Undefined
   *  when every leg succeeded. */
  firstError?: unknown;
}

/**
 * Mark one or more transactions as EXTERNAL (one-sided) transfers with a shared
 * counterparty label — one `POST /external` per transaction, run in parallel.
 * Each leg gains a confirmed one-sided transfer so it drops out of
 * income/expense analytics (net worth keeps it).
 *
 * Uses `Promise.allSettled` rather than `Promise.all`: a partial failure (e.g.
 * one leg lost a `leg_already_transferred` race) must not lose track of the
 * legs that DID succeed. The mutation always resolves — never rejects — with a
 * `MarkExternalTransferResult`; callers branch on `failed > 0`. Queries are
 * always invalidated in `onSettled` since some legs may have been marked even
 * when others failed.
 */
export function useMarkExternalTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      transactionIds: string[];
      counterparty: string;
    }): Promise<MarkExternalTransferResult> => {
      const settled = await Promise.allSettled(
        vars.transactionIds.map((transaction_id) =>
          apiSend<{ transfer: FinanceTransferDto }>('POST', '/apps/finance/transfers/external', {
            transaction_id,
            counterparty: vars.counterparty,
          }),
        ),
      );
      const failures = settled.filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );
      return {
        requested: vars.transactionIds.length,
        marked: settled.length - failures.length,
        failed: failures.length,
        firstError: failures[0]?.reason,
      };
    },
    onSettled: () => invalidateTransfers(qc),
  });
}

export interface TransferMatchResult {
  confirmed: number;
  suggested: number;
}

export function useRunTransferMatching() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiSend<TransferMatchResult>('POST', '/apps/finance/transfers/match', {}),
    onSuccess: () => invalidateTransfers(qc),
  });
}

// ---------------------------------------------------------------------------
// Finance agent suggestions (FIN-11)
// ---------------------------------------------------------------------------
//
// Finance suggestions are platform suggestions whose targetType is a finance
// type (e.g. `finance_transaction`). The platform accept endpoint is
// RECORD-ONLY for finance (ADR 0034): the client applies the proposed change
// via the finance API FIRST, then calls accept to record it. So this file
// exposes the list + a thin record-only accept/reject; the orchestration lives
// in the Review screen.

export type FinanceSuggestionKind =
  | 'finance.categorize'
  | 'finance.transfer_match'
  | 'finance.alert'
  | string;

export interface FinanceSuggestionDto {
  id: string;
  agentId: string;
  kind: FinanceSuggestionKind;
  targetType: string;
  targetId: string;
  proposed: Record<string, unknown>;
  rationale: string;
  confidence: 'low' | 'medium' | 'high' | null;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  expiresAt: string | null;
}

/** Pending finance suggestions targeting transactions (the v1 surface). */
export function useFinanceSuggestions() {
  return useQuery({
    queryKey: [FIN, 'suggestions', { targetType: 'finance_transaction', status: 'pending' }],
    queryFn: () =>
      apiGet<{ suggestions: FinanceSuggestionDto[] }>(
        '/platform/suggestions?targetType=finance_transaction&status=pending',
      ),
    select: (r) => r.suggestions,
    staleTime: 10_000,
  });
}

/** Record-only accept — call this AFTER applying the finance change. */
export function useAcceptFinanceSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ suggestion: FinanceSuggestionDto; decision: { id: string; createdAt: string } }>(
        'POST',
        `/platform/suggestions/${id}/accept`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FIN, 'suggestions'] });
      qc.invalidateQueries({ queryKey: [FIN, 'review-counts'] });
    },
  });
}

export function useRejectFinanceSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ suggestion: FinanceSuggestionDto }>(
        'POST',
        `/platform/suggestions/${id}/reject`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FIN, 'suggestions'] });
      qc.invalidateQueries({ queryKey: [FIN, 'review-counts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Budgets (FIN-12)
// ---------------------------------------------------------------------------

export interface FinanceBudgetDto {
  id: string;
  workspaceId: string;
  categoryId: string;
  month: string; // YYYY-MM
  amountEur: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface BudgetEntry {
  categoryId: string;
  amountEur: number;
}

export interface BudgetCopyResult {
  copied: number;
}

export function useFinanceBudgets(month: string) {
  return useQuery({
    queryKey: [FIN, 'budgets', { month }],
    queryFn: () =>
      apiGet<{ budgets: FinanceBudgetDto[] }>(
        `/apps/finance/budgets?month=${encodeURIComponent(month)}`,
      ),
    select: (r) => r.budgets,
    staleTime: 30_000,
    enabled: /^\d{4}-(0[1-9]|1[0-2])$/.test(month),
  });
}

export function useSetBudgets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { month: string; entries: BudgetEntry[] }) =>
      apiSend<{ budgets: FinanceBudgetDto[] }>('PUT', '/apps/finance/budgets', vars),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [FIN, 'budgets', { month: vars.month }] });
      qc.invalidateQueries({ queryKey: [FIN, 'budget-vs-actual', { month: vars.month }] });
    },
  });
}

export function useCopyBudgets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { fromMonth: string; toMonth: string }) =>
      apiSend<BudgetCopyResult>('POST', '/apps/finance/budgets/copy', vars),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [FIN, 'budgets', { month: vars.toMonth }] });
      qc.invalidateQueries({ queryKey: [FIN, 'budget-vs-actual', { month: vars.toMonth }] });
    },
  });
}

// ---------------------------------------------------------------------------
// Budget-vs-actual (FIN-12)
// ---------------------------------------------------------------------------

export interface BudgetVsActualRow {
  categoryId: string;
  name: string;
  parentId: string | null;
  budget: number;
  actual: number;
  variance: number;
}

export function useBudgetVsActual(month: string, accountIds?: string[]) {
  const { key, param } = accountsKeyAndParam(accountIds);
  return useQuery({
    queryKey: [FIN, 'budget-vs-actual', { month, accounts: key }],
    queryFn: () =>
      apiGet<{ rows: BudgetVsActualRow[] }>(
        `/apps/finance/analytics/budget-vs-actual?month=${encodeURIComponent(month)}${param}`,
      ),
    select: (r) => r.rows,
    staleTime: 30_000,
    enabled: /^\d{4}-(0[1-9]|1[0-2])$/.test(month),
  });
}

// ---------------------------------------------------------------------------
// Analytics — reports + dashboard (FIN-13)
// ---------------------------------------------------------------------------
//
// These consume the FIN-8 analytics endpoints, the SINGLE SOURCE OF TRUTH for
// all income/expense aggregates (design §8). The web NEVER recomputes these
// numbers client-side — charts render exactly what the server returns. Each
// month-ranged endpoint takes a YYYY-MM `from`/`to`; the query is disabled
// until both are syntactically valid so we never fire a 400.

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function validRange(from: string, to: string): boolean {
  return MONTH_RE.test(from) && MONTH_RE.test(to);
}

/**
 * Normalize an optional account-id selection into a stable, sorted key
 * fragment (so query-key identity doesn't depend on selection order) and the
 * `accounts` query param value — omitted entirely when the selection is
 * empty, matching the server's "no filter" convention.
 */
function accountsKeyAndParam(accountIds?: string[]): { key: string[] | null; param: string } {
  if (!accountIds || accountIds.length === 0) return { key: null, param: '' };
  const sorted = [...accountIds].sort();
  return { key: sorted, param: `&accounts=${sorted.map(encodeURIComponent).join(',')}` };
}

export interface CashflowMonthDto {
  month: string;
  moneyIn: number;
  moneyOut: number;
  net: number;
}

export function useCashflow(from: string, to: string, accountIds?: string[]) {
  const { key, param } = accountsKeyAndParam(accountIds);
  return useQuery({
    queryKey: [FIN, 'analytics', 'cashflow', { from, to, accounts: key }],
    queryFn: () =>
      apiGet<{ months: CashflowMonthDto[] }>(
        `/apps/finance/analytics/cashflow?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${param}`,
      ),
    select: (r) => r.months,
    enabled: validRange(from, to),
    staleTime: 30_000,
  });
}

export interface CategoryBreakdownRowDto {
  categoryId: string;
  name: string;
  parentId: string | null;
  kind: FinanceCategoryKind;
  ownTotal: number;
  total: number;
}

export interface CategoryBreakdownDto {
  categories: CategoryBreakdownRowDto[];
  uncategorizedTotal: number;
}

/**
 * Shared query config for one category-breakdown call. Exported so the Reports
 * "category trends" report can fan out one call PER month with `useQueries`
 * while reusing the exact same key + fetcher (and thus the cache) as the hook.
 */
export function categoryBreakdownQueryOptions(
  from: string,
  to: string,
  kind: FinanceCategoryKind,
  options: { enabled?: boolean; accountIds?: string[] } = {},
) {
  const { key, param } = accountsKeyAndParam(options.accountIds);
  return {
    queryKey: [FIN, 'analytics', 'categories', { from, to, kind, accounts: key }] as const,
    queryFn: () =>
      apiGet<CategoryBreakdownDto>(
        `/apps/finance/analytics/categories?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&kind=${kind}${param}`,
      ),
    enabled: validRange(from, to) && (options.enabled ?? true),
    staleTime: 30_000,
  };
}

export function useCategoryBreakdown(
  from: string,
  to: string,
  kind: FinanceCategoryKind,
  options: { enabled?: boolean; accountIds?: string[] } = {},
) {
  return useQuery(categoryBreakdownQueryOptions(from, to, kind, options));
}

export interface NetWorthAccountDto {
  accountId: string;
  name: string;
  balanceEur: number;
}

export interface NetWorthMonthDto {
  month: string;
  assets: number;
  liabilities: number;
  net: number;
  accounts: NetWorthAccountDto[];
}

export function useNetWorth(from: string, to: string, accountIds?: string[]) {
  const { key, param } = accountsKeyAndParam(accountIds);
  return useQuery({
    queryKey: [FIN, 'analytics', 'net-worth', { from, to, accounts: key }],
    queryFn: () =>
      apiGet<{ months: NetWorthMonthDto[] }>(
        `/apps/finance/analytics/net-worth?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${param}`,
      ),
    select: (r) => r.months,
    enabled: validRange(from, to),
    staleTime: 30_000,
  });
}

export interface SavingsFlowMonthDto {
  month: string;
  inflow: number;
  outflow: number;
  net: number;
}

export function useSavingsFlow(from: string, to: string, accountIds?: string[]) {
  const { key, param } = accountsKeyAndParam(accountIds);
  return useQuery({
    queryKey: [FIN, 'analytics', 'savings-flow', { from, to, accounts: key }],
    queryFn: () =>
      apiGet<{ months: SavingsFlowMonthDto[] }>(
        `/apps/finance/analytics/savings-flow?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${param}`,
      ),
    select: (r) => r.months,
    enabled: validRange(from, to),
    staleTime: 30_000,
  });
}

export interface ProfessionalRecoveryMonthDto {
  month: string;
  spent: number;
  recovered: number;
  net: number;
}

export interface ProfessionalRecoveryDto {
  months: ProfessionalRecoveryMonthDto[];
  outstanding: number;
}

/**
 * Professional recovery — returns a 400 `settings_required` when the workspace
 * has not configured professional/reimbursement categories. We surface that
 * via `financeErrorCode(error) === 'settings_required'` rather than retrying.
 */
export function useProfessionalRecovery(
  from: string,
  to: string,
  options: { enabled?: boolean; accountIds?: string[] } = {},
) {
  const { key, param } = accountsKeyAndParam(options.accountIds);
  return useQuery({
    queryKey: [FIN, 'analytics', 'professional-recovery', { from, to, accounts: key }],
    queryFn: () =>
      apiGet<ProfessionalRecoveryDto>(
        `/apps/finance/analytics/professional-recovery?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${param}`,
      ),
    enabled: validRange(from, to) && (options.enabled ?? true),
    // A settings_required 400 is a stable, expected state — do not hammer it.
    retry: false,
    staleTime: 30_000,
  });
}

export interface BalanceEvolutionAccountDto {
  accountId: string;
  name: string;
  high: number;
  low: number;
  source: 'db' | 'calculated';
}

export interface BalanceEvolutionWeekDto {
  weekStart: string; // YYYY-MM-DD (Monday)
  open: number;
  close: number;
  high: number;
  low: number;
  accounts: BalanceEvolutionAccountDto[];
}

const ISO_DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * Weekly per-account balance OHLC candlestick over an ISO date range. Balances
 * are EUR and include transfer legs (ACTUAL balances). `accountIds` optionally
 * filters to selected accounts; passing it changes the query key so each
 * selection is cached separately. Disabled until both dates are valid.
 */
export function useBalanceEvolution(from: string, to: string, accountIds?: string[]) {
  // Normalize the selection so the key is stable regardless of order.
  const accountsKey = accountIds && accountIds.length > 0 ? [...accountIds].sort() : null;
  return useQuery({
    queryKey: [FIN, 'analytics', 'balance-evolution', { from, to, accountIds: accountsKey }],
    queryFn: () => {
      const sp = new URLSearchParams({ from, to });
      if (accountsKey) sp.set('accountIds', accountsKey.join(','));
      return apiGet<{ weeks: BalanceEvolutionWeekDto[] }>(
        `/apps/finance/analytics/balance-evolution?${sp.toString()}`,
      );
    },
    select: (r) => r.weeks,
    enabled: ISO_DATE_RE.test(from) && ISO_DATE_RE.test(to),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Review counts (FIN-11) — sidebar badge + Review screen section badges
// ---------------------------------------------------------------------------

export interface ReviewCountsDto {
  suggestedTransfers: number;
  pendingSuggestions: number;
  uncategorized: number;
}

export function useFinanceReviewCounts(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [FIN, 'review-counts'],
    queryFn: () => apiGet<ReviewCountsDto>('/apps/finance/analytics/review-counts'),
    enabled: options.enabled ?? true,
    staleTime: 15_000,
  });
}
